import { Form, message } from "antd";
import { customParseSelectOptions, getDefaultData } from "../jira/JiraMappingForm";
import _, { isArray, isEmpty, isUndefined } from "lodash";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { MODAL_SIZE_LARGE, SEVERITY_COLOR } from "constants/Layout";
import { SEVERITY_NAMES } from "views/Findings/Constants";
import Vocabulary from "components/Vocabulary";
import BaseModal from "components/BaseModal";
import { hasPermission } from "utils/authService";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { CONFIG_UPDATE_WARNING_MESSAGE } from "../genericIntegrationTicketConstants";
import { integrationTicketingToolsProductOptionsMutator } from "../genericIntegrationTicketUtils";

export const DEFAULT_VALUES_VERSION_ONE = { 
  product: "all", 
  subProduct: ["all"], 
  critical:"High",
  high:"High",
  medium:"Medium",
  low:"Low",
  info:"Low"
};

const VersionOneMappingForm = (
  { loginConfigId, onUpdateConfigComponent, name, displayName, url = undefined, ...props },
  ref
) => {
  const [initialData] = useState(() => getDefaultData(props.config, name));
  const isEdit = Boolean(props.config?.id);
  const [form] = Form.useForm();
  const [data, setData] = useState(initialData);
  const [priorities, setPriorities] = useState([]);
  const thisRef = useRef();
  const repoNamesRef = useRef();
  const teamsRef = useRef();
  const issueTypesRef = useRef();
  const [teams, setTeams] = useState();
  const featureFlags = useFeatureFlag();
  const onOk = async () => {
    await form.validateFields();
    const toolName = name.toUpperCase().replace(" ", "_");
    let data = form.getFieldsValue();
    data.customFields = [];
    const currentRepo = _.find(repoNamesRef.current, (repo) => repo.id === data.projectKey);
    const team = _.find(teamsRef.current, (t) => t.id === data.team);
    const issueType = _.find(issueTypesRef.current, (issue) => issue.id === data.issueType);
    data.customFields.push(
      {
        key: "teamValue",
        value: team?.id,
        name: team?.name,
      },
      {
        key: "issueTypeValue",
        name: issueType?.name,
        value: issueType?.id,
      }
    );

    if (currentRepo) {
      data.customFields.push({
        key: "projectValue",
        type: "textfield",
        dataType: "string",
        name: currentRepo.name,
        value: currentRepo.id,
      });
    }

    const configValues = await generateVersionOneResponse(
      data,
      props.config?.id,
      loginConfigId,
      url,
      toolName,
      thisRef.current?.getCustomFields(),
      featureFlags
    );
    if (!_.isPlainObject(configValues)) {
      return Promise.reject("Mapping already exist");
    }
    await ToolsIntegrationsAPI[!isEdit ? "integrateTicketSystemTool" : "updateTicketSystemToolMapping"](configValues, props.config?.id).promise;
    message.success(`${displayName} Mapping saved`);
    onUpdateConfigComponent && onUpdateConfigComponent(true);
  };

  const onChange = (values) => {
    const formData = { ...data };
    //Don't reset the severity default fields for PagerDuty
    if (
      !isUndefined(values.projectKey) &&
      values.projectKey !== data.projectKey
    ) {
      form.setFieldsValue({ issueType: null });
      formData["issueType"] = null;
    }
    let onChangeHandler = setData;
    onChangeHandler({ ...formData, ...values });
  };

  const appendAll = useCallback(
    (list) => [{ label: "All", value: "all" }, ...(list || [])],
    []
  );

  const getIssueTypes = useCallback(() => {
    if (!data.projectKey) {
      return Promise.resolve([]);
    }
    const { promise, cancel } =
      ToolsIntegrationsAPI.getTicketSystemToolIssueTypes({
        projectKey: data.projectKey,
        loginConfigId,
      });
    promise.then((res) => (issueTypesRef.current = res));
    return { promise, cancel };
  }, [data.projectKey, loginConfigId]);

  const getProjectKeys = useCallback(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getTicketSystemToolProjects(loginConfigId, {
      });

    promise.then((res) => (repoNamesRef.current = res));
    return { promise, cancel };
  }, [loginConfigId]);

  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getTicketSystemToolTeams({
      loginConfigId,
    });

    promise.then((res) => {teamsRef.current = res; setTeams(res)});
    return cancel;
  }, [loginConfigId]);

  useEffect(() => {
    if (data.projectKey && data.issueType) {
      setPriorities([]);
      const { cancel, promise } =
        ToolsIntegrationsAPI.getTicketSystemToolPriorities({
          projectKey: data.projectKey,
          issueType: data.issueType,
          loginConfigId,
        });
      promise.then((response) => {
          const severities = {};
          let severityNames = Object.keys(SEVERITY_NAMES);
          severityNames = severityNames.filter(
            (sn) => sn !== "critical" && sn !== "info"
          );
          response.forEach((entry, index) => {
            severities[severityNames[index]] = entry.id;
            if (severityNames[index] === 'high') { 
              severities['critical'] = entry.id;
            }
            if (severityNames[index] === 'low') { 
              severities['info'] = entry.id;
            }
          });

          if (severityNames.length > response.length) {
            for (
              let index = response.length;
              index < severityNames.length;
              index++
            ) {
              severities[severityNames[index]] =
                severities[severityNames[index - 1]];
            }
          }
          const { critical } = form.getFieldsValue();
          if (_.isUndefined(critical)) {
            form.setFieldsValue(severities);
          }
        setPriorities(response);
      });
      return cancel;
    }
  }, [
    isEdit,
    data.projectKey,
    data.issueType,
    loginConfigId,
    form,
    initialData.projectKey,
    name,
  ]);

  const commonSeverityProps = useMemo(
    () => ({
      colSpan: 5,
      className: "m-b-0",
      type: FieldTypes.SELECT,
      style: { flex: "0 0 20%" },
      rules: [
        {
          required: true,
          message: `Select ${name} Priority`,
        },
      ],
      options: priorities,
      loading: data.projectKey && _.isEmpty(priorities),
      disabled: !data.projectKey,
    }),
    [data.projectKey, name, priorities]
  );

  const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} ${displayName} Mapping`,
    width: MODAL_SIZE_LARGE,
    disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
    onOk,
  };

  // if more than one product or All product is selected then subproduct should be disabled
  // this would work as previous, just will get the product id from array, as product is multi select
  const productId = data.product && data.product[0] !== "all" ? data.product[0] : null;
  const resetSubProductsField = useCallback((values) => { 
    setData(data => ({ ...data, product: values }));
    form.setFieldsValue({subProduct:["all"]});
  }, [form]);
  
  const onCreateSubproduct = useCallback((createdSubproduct) => {
    const subProductValues = form.getFieldValue("subProduct");
    form.setFieldsValue({
      subProduct: [...subProductValues, createdSubproduct.id],
    });
    setData((prevdata) => ({ ...prevdata, subProduct: [...subProductValues, createdSubproduct.id]}));
  }, [form]);

  const onCreateProduct = useCallback((createdProduct) => {
    const productsAfterCreate = [
      ...form.getFieldValue("product"),
      createdProduct.id,
    ];
    form.setFieldsValue({
      product: productsAfterCreate,
      subProduct: ["all"],
    });
    setData((data) => ({
      ...data,
      product: productsAfterCreate,
      subProduct: ["all"],
    }));
  }, [form]);

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        // disabled:isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          optionsMutator: (options) => integrationTicketingToolsProductOptionsMutator(options, form.getFieldValue("product")),
          onChange: resetSubProductsField,
          onCreate: onCreateProduct,
          mode: "multiple"
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: data.product && data.product.length > 1,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onChange: (values) => {
            if (values.includes("all")) {
              setData((prevdata) => ({...prevdata, subProduct: ["all"]}));
              form.setFieldsValue({subProduct: ["all"]});
            } else {
              setData((prevdata) => ({...prevdata, subProduct: values }));
              form.setFieldsValue({subProduct: values});
            }
          },
          onCreate: onCreateSubproduct,
          optionsMutator: (options) => {
            const updatedOptions = appendAll(options);
            const selectedValues = form.getFieldValue("subProduct") || [];
            const isAllSelected = selectedValues.includes("all");
            return updatedOptions.map((option) => ({
              ...option,
              disabled: isAllSelected && option.value !== "all"
            }));
          },
          mode: "multiple"
        },
      },
    ],
    [appendAll, form, productId, resetSubProductsField, onCreateSubproduct, data.product, onCreateProduct]
  );

  const fields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "team",
        label: "Team",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        options: teams,
        props: {
          parseSelectOptions: (options) => customParseSelectOptions(options, name),
          onChange: (value) => setData(data => ({ ...data, team: value })),
        },
      },
      {
        name: "projectKey",
        label: "Project Key",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        props: {
          api: getProjectKeys,
          parseSelectOptions: (options) =>
            customParseSelectOptions(options, name),
          onChange: (value) => {
            isEdit &&
              message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            setData(data => ({ ...data, projectKey: value }));
          },
        },
      },
      {
        name: "issueType",
        label: "Issue Type",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        disabled: !data.projectKey,
        props: {
          api: getIssueTypes,
          parseSelectOptions: (options) =>
            customParseSelectOptions(options, name),
          onChange: (value) => {
            isEdit && message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            form.setFieldsValue({
              critical: undefined,
              high: undefined,
              medium: undefined,
              low: undefined,
              info: undefined,
            });
            setData((data)=> ({ ...data, issueType: value }));
          },
        },
      },
      {
        label: `ArmorCode Severity -> ${displayName} Priority`,
        type: FieldTypes.GROUP,
        fields: [
          {
            name: "critical",
            label: <Vocabulary name="critical"/>,
            ...commonSeverityProps,
          },
          {
            name: "high",
            label: <Vocabulary name="high"/>,
            ...commonSeverityProps,
          },
          {
            name: "medium",
            label: <Vocabulary name="medium"/>,
            ...commonSeverityProps,
          },
          {
            name: "low",
            label: <Vocabulary name="low"/>,
            ...commonSeverityProps,
          },
          {
            name: "info",
            label: <Vocabulary name="info"/>,
            ...commonSeverityProps,
          },
        ],
      },
    ],
    [productSubProductFields, teams, getProjectKeys, data.projectKey, getIssueTypes, displayName, commonSeverityProps, name, isEdit, form]
  );

  return (
    <BaseModal ref={ref} modalProps={modalProps} afterClose={props.afterClose}>
      <Form
        form={form}
        initialValues={data}
        layout="vertical"
        onValuesChange={onChange}
      >
        {generateFormFields(fields)}
      </Form>
    </BaseModal>
  );
};

export default forwardRef(VersionOneMappingForm);

const generateVersionOneResponse = async (
  data,
  id,
  loginConfigId,
  url,
  toolName,
  customFieldsData,
  featureFlags
) => {
  const severities = Object.keys(SEVERITY_COLOR);
  const configValues = _.omit(data, severities);
  configValues.enabled = true;
  configValues.properties = {};
  severities.forEach((s) => {
    if (data[s]) {
      configValues.properties[s] = data[s];
    }
  });
  if (!isUndefined(configValues.transitions)) {
    configValues.transitions = isEmpty(configValues.transitions)
      ? []
      : JSON.parse(configValues.transitions);
  }
  configValues.ticketSystemType = toolName;
  configValues.loginConfigId = loginConfigId;
  if (url) {
    configValues.url = url;
  }
  const isAllProduct = configValues.product[0] === "all";
  const isAllSubproduct = configValues.subProduct[0] === "all";
  if (isAllProduct) {
    delete configValues.product;
  }
  if (isAllSubproduct) {
    delete configValues.subProduct;
  }
  if (isAllProduct && isAllSubproduct) {
    configValues.configurationType = "GLOBAL";
  } else if ((!isAllProduct && isAllSubproduct) || configValues.product.length > 1) {
    configValues.configurationType = "PRODUCT";
  } else {
    configValues.configurationType = "SUBPRODUCT";
  }

  if (customFieldsData) {
    const { inputCustomFields, customFieldState: fieldsChecked } =
      customFieldsData;
    configValues.customFields = [];
    _.forEach(inputCustomFields, (cstmField) => {
      let fieldValue = isArray(data[cstmField.key])
        ? data[cstmField.key].join()
        : data[cstmField.key];

      // cascadingselect: value format `parent:child`
      if (
        cstmField.type === "cascadingselect" &&
        _.isArray(data[cstmField.key])
      ) {
        fieldValue = `${data[cstmField.key][0]}:${data[cstmField.key][1]}`;
      }
      //

      // if fieldValue is mismatch with defaultValue means defaultValue is from template
      const field = {
        ...cstmField,
        active: !!fieldsChecked[cstmField.key],
        defaultVal: fieldValue,
        ...(fieldValue !== cstmField.defaultValue && {
          value: cstmField.defaultValue?.name,
        }),
      };
      if (!_.isEmpty(fieldValue)) {
        configValues[cstmField.key] = fieldValue;
        configValues.customFields.push(field);
      }
    });
  }

  return configValues;
};
